import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { getAllOrganizations, getAllUsers, postCreateSuperAdmin } from '../../../api';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { ORGANIZATION, RolesLowerCase, USER } from '../../../enums';
import styles from './CreateSuperAdminModal.module.scss';
import useDebounce from '../../../helpers/useDebounce';
import { NOTIFICATION } from '../../../enums/ENUMS';
import Search from '../../modules/Search';
import RoleDropdown from './RoleDropDown';
import Modal from '../../modules/Modal';
import { LinearProgress } from '@mui/material';
import { useHotkeys } from 'react-hotkeys-hook';

type TCreateUserModal = {
  open: boolean;
  switchModal: () => void;
  fetchData: () => void;
};

const CreateSuperAdminModal: React.FC<TCreateUserModal> = ({ open, switchModal, fetchData }) => {
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [step, setStep] = useState(1);
  const backToFirstStepButtonRef = useRef<HTMLDivElement>(null);
  const backToSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const toThirdStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [disableInput, setDisableInput] = useState(false);

  const debouncedEmailSearch = useDebounce<string>(newUserEmail, 500);
  const [searchLoading, setSearchLoading] = useState(false);

  const [existUser, setExistUser] = useState(false);
  const checkIfUserExist = async () => {
    try {
      setSearchLoading(true);
      const res = await getAllUsers(0, 9000, newUserEmail);
      const user = res.data.documents.find((u: USER) => u.email === newUserEmail);
      if (user) {
        setExistUser(true);
        if (user?.role?.title === RolesLowerCase.SUPER_ADMIN) {
          notification.showMessage(`User ${user.email} is already a Super Admin`);
        }
        if (user?.username) {
          setNewUserName(user.username);
          setDisableInput(true);
        } else {
          setDisableInput(false);
        }
      } else {
        setExistUser(false);
        setDisableInput(false);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    if (newUserEmail.length) {
      checkIfUserExist();
    }
  }, [debouncedEmailSearch]);

  const addOrgStep = () => {
    setStep(2);
  };

  const onChange = (email: string) => {
    setNewUserEmail(email);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    try {
      // @ts-ignore
      await postCreateSuperAdmin(newUserEmail, selectedOrgs, newUserName);
      notification.showMessage(`Super Admin added: ${newUserEmail}`);
      fetchData();
    } catch (err) {
      console.log(err);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_UPDATED);
      }
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const onClose = () => {
    switchModal();
    setNewUserEmail('');
    setStep(1);
    setEnableSubmit(true);
    setSearchOrgText('');
    setSelectedOrgs([]);
    setNewUserName('');
    setDisableInput(false);
  };

  const [allOrgs, setAllOrgs] = useState<ORGANIZATION[]>([]);
  const fetchAllOrgs = async () => {
    try {
      const res = await getAllOrganizations({ page: 0, limit: 5000, search: '' });
      if (res?.data) {
        setAllOrgs(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchAllOrgs();
  }, []);

  const [orgs, setOrgs] = useState<ORGANIZATION[]>([]);
  const [searchOrgText, setSearchOrgText] = useState('');
  const getOrganizations = async () => {
    try {
      setLoading(true);
      const res = await getAllOrganizations({ page: 0, limit: 1000, search: searchOrgText, selectedType: 'all' });
      console.log(res);
      if (res?.data) {
        setOrgs(res.data.documents);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  const debounce = useDebounce<string>(searchOrgText, 500);
  useEffect(() => {
    getOrganizations();
  }, [debounce]);
  const [selectedOrgs, setSelectedOrgs] = useState<{ organization_id: string; role?: RolesLowerCase }[]>([]);
  const onSelect = (id: string) => {
    if (selectedOrgs.find((org) => org.organization_id === id)) {
      setSelectedOrgs(selectedOrgs.filter((org) => org.organization_id !== id));
    } else {
      setSelectedOrgs([...selectedOrgs, { organization_id: id }]);
    }
  };

  const [enableSubmit, setEnableSubmit] = useState(true);
  useEffect(() => {
    if (selectedOrgs.length) {
      const enableSubmit = selectedOrgs.every((org) => org.role !== undefined);
      setEnableSubmit(enableSubmit);
    } else {
      setEnableSubmit(true);
    }
  }, [selectedOrgs]);

  const handleRoleForOrgSelection = (orgId: string, role: string) => {
    setSelectedOrgs((currentOrgs) => {
      const selectedOrg = currentOrgs.find((obj) => obj.organization_id === orgId);
      if (!selectedOrg) return [...currentOrgs, { organization_id: orgId, role: role as RolesLowerCase }];

      return currentOrgs.map((org) =>
        org.organization_id === orgId ? { ...org, role: role as RolesLowerCase } : org,
      );
    });
  };

  const toFirstStep = () => {
    setStep(1);
    setSelectedOrgs([]);
    setSearchOrgText('');
  };

  const toConfirmStep = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(3);
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        toThirdStepButtonRef?.current?.click();
        break;
      case 3:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  useHotkeys('Backspace', () => {
    if (!open || loading) return;

    switch (step) {
      case 2:
        backToFirstStepButtonRef?.current?.click();
        break;
      case 3:
        backToSecondStepButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        {step !== 3 && <div className={styles.modalHeader}>Add new Super Admin</div>}

        {step === 1 && (
          <form className={styles.form} onSubmit={toConfirmStep}>
            <div className={styles.list}>
              <div className={styles.fieldContainer}>
                <input
                  disabled={loading}
                  placeholder="Email"
                  onChange={(e) => onChange(e.target.value)}
                  value={newUserEmail}
                  required
                  type="email"
                />
                <input
                  disabled={loading || disableInput}
                  placeholder="Username"
                  required
                  type="text"
                  onChange={(e) => setNewUserName(e.target.value)}
                  value={newUserName}
                />
              </div>
            </div>

            <div className={styles.customPadding}>
              {newUserEmail.length > 0 && (
                <div className={styles.addMore} onClick={addOrgStep}>
                  + Add Organisation?
                </div>
              )}

              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading || !newUserEmail.length || !newUserName.length} type="submit">
                  <div ref={toSecondStepButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
              </div>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className={styles.orgForm} onSubmit={toConfirmStep}>
            <div className={styles.orgHeading}> Select Organisation </div>
            <Search className={styles.searchOrg} onChange={(e) => setSearchOrgText(e.target.value)} placeHolder="Search by Name" />

            <div className={styles.orgList} id="org_list">
              {orgs.map((org, orgIndex) => (
                <div key={org.id} className={styles.org} onClick={() => onSelect(org.id)}>
                  <div className={styles.orgTitle}>{org.name}</div>
                  <div className={styles.orgRight}>
                    {' '}
                    <RoleDropdown
                      onSelect={(role: string) => handleRoleForOrgSelection(org.id, role)}
                      selectedRole={selectedOrgs.find((obj) => obj.organization_id === org.id)?.role}
                    />
                    <input
                      className={styles.orgCheck}
                      checked={!!selectedOrgs.find((obj) => obj.organization_id === org.id)}
                      type="checkbox"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.customPadding}>
              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading || !newUserEmail.length || !newUserName.length || !enableSubmit} type="submit">
                  <div ref={toThirdStepButtonRef}>Create</div>
                </ContainedButton>
                <OutlinedButton onClick={toFirstStep}>
                  <div ref={backToFirstStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </div>
          </form>
        )}

        {step === 3 && (
          <form className={styles.orgForm} onSubmit={onSubmit}>
            <div className={styles.orgHeading} style={{ fontSize: '18px', marginBottom: '22px' }}>
              {' '}
              Are you sure that you want to add {newUserEmail} as Super Admin?{' '}
            </div>

            {selectedOrgs.length > 0 && allOrgs.length > 0 && (
              <>
                Here is the list of Organisations and roles you selected:
                <ul className={styles.orgList} style={{ paddingLeft: '30px' }}>
                  {selectedOrgs.map((org) => (
                    <li style={{ filter: 'opacity(0.6)' }}>
                      {allOrgs.find((obj) => obj.id === org.organization_id)?.name} -{' '}
                      {org.role === RolesLowerCase.ORGANIZATION_ADMIN ? 'OA' : org.role}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className={styles.customPadding}>
              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading} type="submit">
                  <div ref={submitButtonRef}>Add as Super Admin</div>
                </ContainedButton>
                <OutlinedButton disabled={loading} onClick={toFirstStep}>
                  <div ref={backToSecondStepButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </div>
          </form>
        )}
      </div>
      {searchLoading && <LinearProgress />}
    </Modal>
  );
};

export default CreateSuperAdminModal;
